"use client";

import { useState } from "react";
import { Modal, Button as AntButton, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { userDeleteAccount } from "../../../apiservice/userService";

const { confirm } = Modal;

export default function DeleteAccountPage() {
  const [email, setEmail] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showDeleteConfirm = () => {
    if (!email.trim()) {
      messageApi.error("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      messageApi.error("Please enter a valid email address");
      return;
    }

    confirm({
      title: "Delete Your Account",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div className="py-4">
          <p className="mb-3">Are you sure you want to delete your account?</p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-md mb-3">
            <p className="text-sm text-red-800 dark:text-red-800 m-0">
              <strong>This action cannot be undone.</strong> This will
              permanently delete your account and remove all associated data.
            </p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 m-0">
            Email:{" "}
            <strong className="text-gray-600 dark:text-gray-400">
              {email}
            </strong>
          </p>
        </div>
      ),
      okText: "Yes, Delete My Account",
      okType: "danger",
      cancelText: "Cancel",
      width: 500,
      onOk() {
        handleDeleteAccount();
      },
      onCancel() {
        messageApi.info("Account deletion cancelled");
      },
    });
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);

    try {
      // Perform account deletion using axios with await
      await userDeleteAccount({});
      alert(
        "Your delete request has been created. An email will be sent to you once your account has been fully deleted from our system. You don't need to perform any other action."
      );
      setIsDeleting(false);
      setDeleteRequested(true);
      messageApi.success("Account deletion request sent successfully");
    } catch (err) {
      console.error("Account deletion failed", err);
      alert(
        "Failed to send the delete request. Please try again, or go to your App > Profile > Delete Account, to delete your account"
      );
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setDeleteRequested(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300">
      {contextHolder}

      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold">
            Keyway Delete Account Request
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8">
        {!deleteRequested ? (
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg shadow-sm">
            {/* Header */}
            <div className="text-center p-6 pb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🗑️</span>
              </div>
              <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-2">
                Delete Account Request For Keyway App <br /> (From Business
                Yield)
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Permanently delete your account and all associated data
              </p>
            </div>

            <div className="px-6 pb-6 space-y-6">
              {/* Warning Alert */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 dark:text-amber-400 text-lg">
                    ⚠️
                  </span>
                  <div>
                    <p className="text-amber-800 dark:text-amber-200 font-medium mb-1">
                      Warning: This action is irreversible.
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      All your data, including Profile Information, Preferences,
                      and Usage History and Memebership will be permanently
                      deleted.
                    </p>
                  </div>
                </div>
              </div>

              {/* What will be deleted */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <span>🛡️</span>
                  What will be deleted:
                </h3>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 pl-4">
                  <li>• All Your Profile Information</li>
                  <li>• All Your preferences</li>
                  <li>• Usage history and analytics</li>
                  <li>• Membership and Subscription History</li>
                </ul>
              </div>

              {/* Email Input */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Enter your email address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isDeleting}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  We'll send a confirmation email to verify the deletion
                  request.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => window.history.back()}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <AntButton
                  type="primary"
                  danger
                  size="large"
                  loading={isDeleting}
                  onClick={showDeleteConfirm}
                  className="flex-1 h-12"
                >
                  {isDeleting ? "Processing..." : "Delete Account"}
                </AntButton>
              </div>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 border border-green-200 dark:border-green-800 rounded-lg shadow-sm">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📧</span>
              </div>

              <h2 className="text-2xl font-semibold text-green-600 dark:text-green-400 mb-4">
                Deletion Request Sent
              </h2>

              <div className="space-y-4 text-gray-600 dark:text-gray-400 mb-8">
                <p>
                  An email will be sent to{" "}
                  <strong className="text-gray-900 dark:text-white">
                    {email}
                  </strong>
                </p>
                <p>
                  Please check your inbox and click the confirmation link to
                  proceed with the account deletion, this confirmation email may
                  take up to 30 minutes before you may get it.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg text-sm">
                  <p className="text-blue-800 dark:text-blue-200 m-0">
                    <strong>Next steps:</strong> The email contains a secure
                    link that you need to visit to complete the deletion
                    process. If you don't see the email, please check your spam
                    folder, after 30 Minutes.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={resetForm}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Submit Another Request
                </button>
                {/* <button
                  onClick={() => (window.location.href = "/")}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  Return to Dashboard
                </button> */}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
